import React, {useCallback, useEffect, useMemo, useState} from 'react'
import moment from 'moment'
import axios from 'axios'
import MetaTags from '../../../../meta/MetaTags'
import DataTableList from '../../components/DataTableList'
import {DOCTORS_BILLING_REPORT_HEADERS} from '../../../../constants/RoleConstants'
import {USER_ROLES} from '../../../../constants/StatusConstants'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import FilterComponent from '../../components/filters/FilterComonent'
import {useAdminBillingReport} from '../../../../hooks/useAdminBillingReport'
import {saveAs} from 'file-saver'
import * as XLSX from 'xlsx'
import {toast} from 'react-toastify'

const AdminBillingReport: React.FC = () => {
  const [viewType, setViewType] = useState<'weekly' | 'monthly'>('monthly')
  const [monthYear, setMonthYear] = useState(() => moment().format('MMMM YYYY'))

  const [doctors, setDoctors] = useState<
    Array<{id: number; uuid: string; first_name: string; last_name: string}>
  >([])
  const [selectedDoctorId, setSelectedDoctorId] = useState<number | undefined>(undefined)
  const [filteredData, setFilteredData] = useState<any[]>([])

  const {data, loading, error, fetchReport} = useAdminBillingReport(monthYear)

  useEffect(() => {
    axios
      .get(API_ENDPOINTS.GET_ALL_DOCTORS())
      .then((resp) => {
        const fetchedDoctors = resp.data.data?.doctors || resp.data.data || []
        setDoctors(fetchedDoctors)
      })
      .catch(() => {
        setDoctors([])
      })
  }, [])

  useEffect(() => {
    fetchReport(monthYear, selectedDoctorId)
  }, [fetchReport, monthYear, selectedDoctorId])

  const onFilterChange = useCallback((_view: 'weekly' | 'monthly', start: moment.Moment) => {
    const next = start.format('MMMM YYYY')
    setViewType(_view)
    setMonthYear(next)
  }, [])

  // Apply month + doctor filters
  useEffect(() => {
    const month = monthYear
    const next = (data || []).filter((item: any) => {
      const cycleStart = item.cycle_start ? moment(item.cycle_start) : null
      const cycleEnd = item.cycle_end ? moment(item.cycle_end) : null

      const startsInMonth = cycleStart && cycleStart.format('MMMM YYYY') === month
      const endsInMonth = cycleEnd && cycleEnd.format('MMMM YYYY') === month

      const isInMonth = startsInMonth || endsInMonth
      const matchesDoctor = !selectedDoctorId || Number(item.doctor_id) === Number(selectedDoctorId)

      return isInMonth && matchesDoctor
    })

    setFilteredData(next)
  }, [data, monthYear, selectedDoctorId])

  // Prepare CSV export
  const exportReport = useMemo(() => {
    const excludedColumns: string[] = []

    const filteredHeaders = DOCTORS_BILLING_REPORT_HEADERS.filter(
      (col) => !excludedColumns.includes(col.name)
    )

    const headers = filteredHeaders.flatMap((col) =>
      col.key === 'patient' ? [col.name, 'DOB'] : [col.name]
    )

    const rows = filteredData.map((row: any) => {
      const rowData: Record<string, any> = {}

      filteredHeaders.forEach((col) => {
        if (col.key === 'patient') {
          rowData[col.name] =
            typeof col.selector === 'function' ? col.selector(row) : (row as any)[col.key]
          rowData.DOB = row?.dob ? moment(row.dob).format('MM/DD/YYYY') : ''
          return
        }

        rowData[col.name] =
          typeof col.selector === 'function' ? col.selector(row) : (row as any)[col.key]
      })

      return rowData
    })

    return {headers, rows}
  }, [filteredData])

  const exportToCSV = useCallback(() => {
    try {
      const ws = XLSX.utils.json_to_sheet(exportReport.rows, {header: exportReport.headers})
      const csv = XLSX.utils.sheet_to_csv(ws)

      const formattedDate = moment(monthYear, 'MMMM YYYY').format('MM_YY')

      const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'})
      saveAs(blob, `CandiHealth_AllBilling_${formattedDate}.csv`)
    } catch {
      toast.error('CSV export failed')
    }
  }, [exportReport, monthYear])

  return (
    <>
      <MetaTags title='All Billing Reports' />

      <div className='d-flex justify-content-end align-items-center mb-2'>
        <div className='w-25 me-3'>
          <label htmlFor='doctorSelect' className='form-label'>
            Select Doctor
          </label>
          <select
            id='doctorSelect'
            className='form-select'
            value={selectedDoctorId ?? ''}
            onChange={(e) => {
              const val = e.target.value
              setSelectedDoctorId(val ? Number(val) : undefined)
            }}
          >
            <option value=''>All Doctors</option>
            {doctors.map((d) => (
              <option key={d.id} value={d.id}>
                {d.first_name} {d.last_name}
              </option>
            ))}
          </select>
        </div>
        <div className='d-flex flex-column align-items-center'>
          <FilterComponent
            startDate={moment(monthYear, 'MMMM YYYY')}
            endDate={moment(monthYear, 'MMMM YYYY').endOf('month')}
            viewType={viewType}
            showDurationButton={false}
            onFilterChange={onFilterChange}
          />
        </div>
      </div>

      {error && <div className='text-danger mb-3'>{error}</div>}

      {/* Note */}
      <div className='bg-light text-dark d-flex align-items-center border rounded p-3 mb-3'>
        <span className='me-2 text-primary'>
          <i className='bi bi-info-circle-fill' style={{fontSize: '1.3rem'}}></i>
        </span>
        <div>
          <strong>Note:</strong> You may see multiple entries for patients whose cycle ends and
          another starts within the same month.
        </div>
      </div>

      <DataTableList
        title='All Billing Reports'
        columns={DOCTORS_BILLING_REPORT_HEADERS}
        data={filteredData as any}
        isLoading={loading}
        showAddButton={false}
        showExportButton={filteredData.length > 0 && !loading}
        onExportClick={exportToCSV}
        allowedRoles={[USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN]}
      />
    </>
  )
}

export default AdminBillingReport
